import {
  FileText,
  Globe,
  Linkedin,
  type LucideIcon,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Minimal source-provenance indicator for a single deal-info field.
 *
 * Renders a tiny `·{count}` superscript next to the field label. Hovering
 * the trigger opens a tooltip listing each source with its file-type icon
 * and a short label. Items are intentionally NOT clickable — this is
 * purely a "where did the AI get this from?" disclosure.
 *
 * Scoped to a single field — the legacy SourceBadge is dialog-style and
 * far too heavy for the dense Profile-tab rows.
 */

export interface FieldSource {
  text?: string;
  file_type?: string;
  source_type?: string;
  heading?: string;
  file_url?: string;
  source_description?: string;
}

const sourceIcon = (source: FieldSource): LucideIcon => {
  const type = (source.source_type || source.file_type || "").toLowerCase();
  if (type.includes("linkedin")) return Linkedin;
  if (type.includes("web") || type.includes("url") || type.includes("http"))
    return Globe;
  return FileText;
};

const sourceLabel = (source: FieldSource): string => {
  if (source.heading) return source.heading;
  if (source.file_url) {
    try {
      const u = new URL(
        source.file_url.startsWith("http")
          ? source.file_url
          : `https://${source.file_url}`,
      );
      const last = u.pathname.split("/").filter(Boolean).pop();
      return last || u.hostname;
    } catch {
      return source.file_url;
    }
  }
  return (
    source.source_description ||
    source.source_type ||
    source.file_type ||
    "Source"
  );
};

export function FieldSources({ sources }: { sources?: FieldSource[] }) {
  if (!sources || sources.length === 0) return null;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            aria-label={`${sources.length} source${
              sources.length > 1 ? "s" : ""
            }`}
            className="inline-flex h-4 min-w-4 cursor-default items-center justify-center rounded-full px-1 font-mono text-[10px] tabular-nums text-muted-foreground/70 transition-colors hover:bg-foreground/[0.06] hover:text-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            ·{sources.length}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          // Override the default ink-on-white tooltip surface — the
          // popover-style light background reads better for a
          // multi-line list of items than the standard dark tooltip.
          className="max-w-xs border border-border bg-popover p-2 text-xs text-popover-foreground shadow-md"
        >
          <p className="px-1 pb-1.5 text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
            {sources.length} source{sources.length > 1 ? "s" : ""}
          </p>
          <ul className="space-y-0.5">
            {sources.map((s, i) => {
              const Icon = sourceIcon(s);
              const label = sourceLabel(s);
              return (
                <li
                  key={`${s.file_url ?? label}-${i}`}
                  className="flex items-center gap-2 rounded-md px-1.5 py-1"
                >
                  <Icon className="h-3 w-3 shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate text-foreground">
                    {label}
                  </span>
                </li>
              );
            })}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
